import Vimeo from '@vimeo/player';

/**
 * A video loader is a class that provides the functionality of playing and pausing a video
 * Depending on the video source (native, youtube, vimeo...),
 * the new class may have its own implementation of IVideoLoader's methods
 */
export interface IVideoLoader {
    play: Function;
    pause: Function;
    playSafe: Function; // will handle error if interrupted by pause;
    setOnPlay: Function;
    setOnPause: Function;
    triggerAutoPlay: (callbackWhenFail?: Function) => Promise<void>;
}

const dummyFn = () => {};
export const DefaultVideoLoader = {
    play: dummyFn,
    pause: dummyFn,
    playSafe: dummyFn,
    setOnPlay: dummyFn,
    setOnPause: dummyFn,
    triggerAutoPlay: () => new Promise<void>(resolve => { resolve(); })
};

abstract class BaseVideoLoader implements IVideoLoader {
    protected onPlay: Function;
    protected onPause: Function;

    setOnPlay(onPlay: Function) {
        this.onPlay = onPlay;
    }

    setOnPause(onPause: Function) {
        this.onPause = onPause;
    }

    abstract play(): void;
    abstract pause(): void;
    abstract playSafe(): void;

    triggerAutoPlay(): Promise<void> {
        // to be implemented only if necessary
        return DefaultVideoLoader.triggerAutoPlay();
    }
}

export class HTMLVideoLoader extends BaseVideoLoader {
    private video: HTMLVideoElement;

    constructor(element: HTMLVideoElement) {
        super();
        this.video = element;
        this.video.addEventListener('play', () => {
            if (this.onPlay) {
                this.onPlay();
            }
        });
        this.video.addEventListener('pause', () => {
            if (this.onPause) {
                this.onPause();
            }
        });
        this.video.addEventListener('ended', () => {
            if (this.onPause) {
                this.onPause();
            }
        });
    }

    play(): Promise<void> {
        return this.video.play();
    }

    pause() {
        this.video.pause();
    }

    async playSafe() {
        try {
            await this.video.play();
        } catch {
            // silent error case interrupted by pause
        }
    }

    async triggerAutoPlay(callbackWhenFail?: Function): Promise<void> {
        try {
            await this.play();
            this.video.setAttribute('autoplay', 'true');
        } catch (_error) {
            if (callbackWhenFail) {
                callbackWhenFail();
            }
        }
    }
}

const SCRIPT_TAG = "script";

window["onYouTubeIframeAPIReady"] = () => { };

interface IYoutubeBlockMap {
    [iframeId: string]: IYoutubeBlock;
}

interface IYoutubePlayerMap {
    [iframeId: string]: YT.Player;
}

interface IYoutubeBlock {
    iframe: HTMLIFrameElement;
    onReady?: YT.PlayerEventHandler<YT.PlayerEvent>;
    onStateChange?: YT.PlayerEventHandler<YT.OnStateChangeEvent>;
}

export class YoutubeFactory {
    private static instance: YoutubeFactory;
    private youtubePlayerMap: IYoutubePlayerMap = {};
    private youtubeBlockMap: IYoutubeBlockMap = {};

    constructor() {
        const tag = document.createElement(SCRIPT_TAG);
        tag.src = "https://www.youtube.com/iframe_api";
        const firstScriptTag = document.getElementsByTagName(SCRIPT_TAG)[0];
        firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    }

    public static getInstance(): YoutubeFactory {
        if (!this.instance) {
            this.instance = new YoutubeFactory();
            return this.instance;
        }
        return this.instance;
    }

    addEventListener(iframeId: string, iframe: IYoutubeBlock) {
        if (!this.youtubeBlockMap[iframeId]) {
            this.youtubeBlockMap[iframeId] = iframe;
        } else {
            console.error(`[YoutubeFactory]: duplicated iframe id: ${iframeId}`);
        }
    }

    removeEventListener(iframeId: string) {
        if (this.youtubeBlockMap[iframeId]) {
            delete this.youtubeBlockMap[iframeId];
            delete this.youtubePlayerMap[iframeId];
        }
    }

    initialize() {
        window["onYouTubeIframeAPIReady"] = () => {
            Object.keys(this.youtubeBlockMap).forEach((iframeId) => {
                const { onReady, onStateChange } = this.youtubeBlockMap[iframeId];
                this.youtubePlayerMap[iframeId] = new YT.Player(iframeId, {
                    events: {
                        onReady: onReady || (() => { }),
                        onStateChange: onStateChange || (() => { })
                    },
                });
            });
        };
    }

    getYoutubePlayer(iframeId: string): YT.Player {
        return this.youtubePlayerMap[iframeId];
    }
}

export interface IYoutubeLoaderOptions {
    playWhenReady?: boolean;
}

export class YoutubeLoader extends BaseVideoLoader {
    private video: HTMLIFrameElement;
    private get player(): YT.Player {
        if (!this.video) {
            return null;
        }
        const factory = YoutubeFactory.getInstance();
        return factory.getYoutubePlayer(this.video.id);
    }

    constructor(element: HTMLIFrameElement) {
        super();
        this.video = element;
        const factory = YoutubeFactory.getInstance();
        factory.addEventListener(element.id, {
            iframe: element,
            onStateChange: (event: YT.OnStateChangeEvent) => {
                const { data } = event;
                if (data == YT.PlayerState.PLAYING) {
                    if (this.onPlay) {
                        this.onPlay();
                    }
                } else if (data == YT.PlayerState.PAUSED || data == YT.PlayerState.ENDED) {
                    if (this.onPause) {
                        this.onPause();
                    }
                }
            }
        });
        factory.initialize();
    }

    play() {
        if (this.player) {
            this.player.playVideo();
        }
    }

    pause() {
        if (this.player) {
            this.player.pauseVideo();
        }
    }

    playSafe() {
        this.play();
    }
}

export class VimeoLoader extends BaseVideoLoader {
    private player: Vimeo;

    constructor(element: HTMLIFrameElement) {
        super();
        this.player = new Vimeo(element);
        this.player.on('play', () => {
            if (this.onPlay) {
                this.onPlay();
            }
        });
        this.player.on('pause', () => {
            if (this.onPause) {
                this.onPause();
            }
        });
        this.player.on('ended', () => {
            if (this.onPause) {
                this.onPause();
            }
        });
    }

    play() {
        if (this.player) {
            this.player.play();
        }
    }

    pause() {
        if (this.player) {
            this.player.pause();
        }
    }

    async playSafe() {
        if (this.player) {
            try {
                await this.player.play();
            } catch {
                // silent error case interrupted by pause
            }
        }
    }
}

export const VideoType = {
    Youtube: 'youtube',
    Vimeo: 'vimeo'
};

export function lazyPoster() {
    const DATA_POSTER_ATTRIBUTE = 'data-poster';
    Array.prototype.slice.call(document.querySelectorAll(`video[${DATA_POSTER_ATTRIBUTE}]`)).forEach((video) => {
        const posterUrl = video.getAttribute(DATA_POSTER_ATTRIBUTE);
        if (posterUrl) {
            video.setAttribute('poster', posterUrl);
        }
    });
};
